import Loadable from 'components/Loadable';
import { type PlaybackController } from 'components/VideoPlayer/controller/PlaybackController';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import { type UseBandwidthWarning } from 'components/VideoPlayer/hooks/useBandwidthWarning';
import { useControllerState } from 'solox';
import useLocale from 'src/hooks/useLocale';
import { useVideoPlayerIsLoading } from './players/hooks/useVideoPlayerIsLoading';

export const PlayerDebugPanel = Loadable({
  loader: () => import('src/core/video/components/PlayerDebug/PlayerDebug'),
});

export interface PlayerOverlayCommonProps {
  playbackController: PlaybackController;
  playingCamera: PlayingCamera;
  locationIsLocal: boolean;
  debugMode?: boolean;
  bandwidthWarning?: UseBandwidthWarning;
}

/**
 * The loading flag, the stream, and the debug snapshot shared by the legacy
 * and Aspen player overlays.
 */
export function usePlayerOverlayCommon({
  playbackController,
  playingCamera,
  debugMode,
}: Pick<PlayerOverlayCommonProps, 'playbackController' | 'playingCamera' | 'debugMode'>) {
  const [loading] = useVideoPlayerIsLoading(playbackController, playingCamera);
  const locale = useLocale();

  const { debugInfo, stream } = useControllerState(playbackController.state, (state) => {
    const player = state.byPlayerId[playingCamera.playerId];
    const debugInfo =
      player && debugMode
        ? {
            playerState: player.state,
            playerStatus: player.status,
          }
        : undefined;

    return {
      debugInfo,
      stream: player?.state.streamInfo,
    };
  });

  return {
    loading,
    stream,
    debugInfo,
    noStreamMessage: locale.playerMessage.noStreamAvailable,
  };
}
